import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, ExternalLink, Star } from "lucide-react";
import api from "@/lib/api";
import { PageShell } from "@/components/PageShell";

const TIERS = {
  platinum: { label: "Platinum Partners", ring: "ring-slate-300", badge: "bg-slate-800 text-white" },
  gold: { label: "Gold Sponsors", ring: "ring-yellow-300", badge: "bg-yellow-400 text-slate-900" },
  silver: { label: "Silver Sponsors", ring: "ring-slate-200", badge: "bg-slate-300 text-slate-800" },
  hole: { label: "Hole Sponsors", ring: "ring-green-200", badge: "bg-green-100 text-green-700" },
};

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => { api.get("/sponsors").then((r) => setSponsors(r.data)); }, []);

  const grouped = Object.keys(TIERS).map((tier) => ({ tier, items: sponsors.filter((s) => s.tier === tier) }));

  return (
    <PageShell>
      <div className="sky-gradient text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 text-center">
          <Award className="mx-auto mb-4" size={36} />
          <h1 className="font-display text-4xl lg:text-5xl">Our Sponsors</h1>
          <p className="mt-3 text-white/90 max-w-xl mx-auto text-lg">The Classic simply wouldn't happen without the generosity of the businesses who support the day in Pow's memory.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 space-y-14">
        {grouped.map(({ tier, items }) => items.length > 0 && (
          <section key={tier}>
            <div className="flex items-center gap-3 mb-6">
              <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ${TIERS[tier].badge}`}>{TIERS[tier].label}</span>
              <div className="h-px flex-1 bg-slate-100" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {items.map((s) => (
                <div key={s.id} data-testid={`sponsor-${s.id}`} className="rounded-2xl border border-slate-100 bg-white p-7 hover:-translate-y-1 hover:shadow-lg transition-all">
                  <div className={`h-16 w-16 rounded-2xl grid place-items-center ring-4 ${TIERS[tier].ring} bg-sky-50 mb-4`}>
                    {s.logo_url ? <img src={s.logo_url} alt={s.name} className="h-full w-full object-contain rounded-2xl" /> : <Star className="text-sky-500" />}
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900">{s.name}</h3>
                  {s.description && <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.description}</p>}
                  {s.website && s.website !== "#" && (
                    <a href={s.website} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:gap-2 transition-all">
                      Visit <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}

        <div className="rounded-3xl bg-slate-900 text-white p-10 lg:p-14 text-center">
          <h2 className="font-display text-3xl">Become a Hole Sponsor</h2>
          <p className="mt-3 text-slate-300 max-w-lg mx-auto">From €100 per hole — includes a sign recognising your business at the tee box. Get in touch to support the day.</p>
          <Link to="/donate" className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-green-600 font-semibold hover:-translate-y-0.5 transition-all">Support the Classic</Link>
        </div>
      </div>
    </PageShell>
  );
}
